/**
 * Lightweight single-object 3D viewer, used inside an expanded gallery card.
 *
 * Unlike the old full-page Scene3D, this renders exactly one object (procedural
 * geometry or a loaded GLTF model) into a small canvas, with simple lighting
 * and a slow auto-rotate. One instance is created per expanded item and
 * disposed when the item collapses.
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

export interface ModelDef {
  /** Geometry factory — used when `modelPath` is not provided or modelUseMaterial is false */
  geometry?: () => THREE.BufferGeometry;
  /** Material factory — used with `geometry`. Defaults to a neutral physical material */
  material?: () => THREE.Material;
  /** Optional path to a GLTF model (overrides geometry when provided) */
  modelPath?: string;
  /** Uniform scale applied to the loaded GLTF model (default: 1) */
  modelScale?: number;
  /** When true, the model's own materials/textures are preserved instead of using `material` */
  modelUseMaterial?: boolean;
  /** Degrees to tilt the object before spinning around Y. Default: 0 */
  spinTilt?: number;
}

export class ModelViewer {
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer;
  private object: THREE.Object3D | null = null;
  private animationFrameId: number | null = null;
  private resizeObserver: ResizeObserver;
  private resizeScheduled = false;
  private disposed = false;

  constructor(private canvas: HTMLCanvasElement) {
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(40, 1, 0.1, 100);
    this.camera.position.set(0, 1.1, 4.4);
    this.camera.lookAt(0, 0, 0);

    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    this.setupLighting();

    this.resizeObserver = new ResizeObserver(() => this.scheduleResize());
    this.resizeObserver.observe(canvas);
    this.onResize();

    this.animate();
  }

  private setupLighting(): void {
    this.scene.add(new THREE.AmbientLight(0xffffff, 0.55));

    const hemisphereLight = new THREE.HemisphereLight(0xf8fbff, 0x7b8794, 0.7);
    hemisphereLight.position.set(0, 5, 0);
    this.scene.add(hemisphereLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 1.4);
    directionalLight.position.set(3, 4, 3);
    this.scene.add(directionalLight);
  }

  /**
   * ResizeObserver callbacks that synchronously trigger further layout can
   * cause the browser to report "ResizeObserver loop completed with
   * undelivered notifications" — deferring the actual resize to the next
   * animation frame avoids that.
   */
  private scheduleResize(): void {
    if (this.resizeScheduled) return;
    this.resizeScheduled = true;
    requestAnimationFrame(() => {
      this.resizeScheduled = false;
      if (!this.disposed) this.onResize();
    });
  }

  private onResize(): void {
    const rect = this.canvas.getBoundingClientRect();
    const width = Math.max(1, Math.round(rect.width));
    const height = Math.max(1, Math.round(rect.height));

    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  }

  /** Load and display an object described by a gallery item's model definition. */
  async loadFromDef(def: ModelDef): Promise<void> {
    let object: THREE.Object3D;

    if (def.modelPath) {
      if (def.modelUseMaterial) {
        object = await this.loadGLTFGroup(def.modelPath, def.modelScale ?? 1);
      } else {
        const geometry = await this.loadGLTFGeometry(def.modelPath, def.modelScale ?? 1);
        object = new THREE.Mesh(geometry, def.material?.() ?? this.defaultMaterial());
      }
    } else if (def.geometry) {
      object = new THREE.Mesh(def.geometry(), def.material?.() ?? this.defaultMaterial());
    } else {
      return;
    }

    if (this.disposed) {
      this.disposeObject(object);
      return;
    }

    this.setObject(object, def.spinTilt ?? 0);
  }

  private defaultMaterial(): THREE.Material {
    return new THREE.MeshStandardMaterial({ color: 0x888480, metalness: 0.4, roughness: 0.5 });
  }

  private setObject(object: THREE.Object3D, spinTilt: number): void {
    if (this.object) {
      this.scene.remove(this.object);
      this.disposeObject(this.object);
    }

    if (spinTilt !== 0) {
      object.rotation.x = THREE.MathUtils.degToRad(spinTilt);
    }

    this.object = object;
    this.scene.add(object);
    this.frameObject(object);
  }

  /** Position the camera so the entire object's bounding box fits in view. */
  private frameObject(object: THREE.Object3D): void {
    object.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(object);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const radius = Math.max(size.x, size.y, size.z, 0.001) / 2;

    const fov = THREE.MathUtils.degToRad(this.camera.fov);
    const paddingFactor = 1.2; // slight breathing room around the model
    const distance = (radius / Math.sin(fov / 2)) * paddingFactor;

    const direction = new THREE.Vector3(0, 0.35, 1).normalize();
    this.camera.position.copy(center.clone().addScaledVector(direction, distance));
    this.camera.near = Math.max(distance / 100, 0.01);
    this.camera.far = distance * 100;
    this.camera.lookAt(center);
    this.camera.updateProjectionMatrix();
  }

  private disposeObject(object: THREE.Object3D): void {
    object.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry?.dispose();
        const material = child.material;
        if (Array.isArray(material)) {
          material.forEach((m) => this.disposeMaterial(m));
        } else if (material) {
          this.disposeMaterial(material);
        }
      }
    });
  }

  private disposeMaterial(material: THREE.Material): void {
    const mapped = material as THREE.Material & Record<string, unknown>;
    for (const key of ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap', 'emissiveMap']) {
      const value = mapped[key];
      if (value instanceof THREE.Texture) value.dispose();
    }
    material.dispose();
  }

  private async loadGLTFGroup(path: string, scale: number): Promise<THREE.Group> {
    return new Promise((resolve, reject) => {
      new GLTFLoader().load(
        path,
        (gltf) => {
          const group = gltf.scene;
          if (scale !== 1) group.scale.setScalar(scale);

          group.updateMatrixWorld(true);
          const box = new THREE.Box3().setFromObject(group);
          const center = box.getCenter(new THREE.Vector3()).divideScalar(scale);
          group.children.forEach((child) => child.position.sub(center));

          group.traverse((child) => {
            if (child instanceof THREE.Mesh) {
              child.castShadow = true;
              child.receiveShadow = true;
            }
          });

          resolve(group);
        },
        undefined,
        (error) => reject(error)
      );
    });
  }

  private async loadGLTFGeometry(path: string, scale: number): Promise<THREE.BufferGeometry> {
    return new Promise((resolve, reject) => {
      new GLTFLoader().load(
        path,
        (gltf) => {
          const geometries: THREE.BufferGeometry[] = [];
          gltf.scene.updateMatrixWorld(true);
          gltf.scene.traverse((child) => {
            if (child instanceof THREE.Mesh) {
              const geometry = (child.geometry as THREE.BufferGeometry).clone();
              geometry.applyMatrix4(child.matrixWorld);
              geometries.push(geometry);
            }
          });

          if (geometries.length === 0) {
            reject(new Error('No mesh found in GLTF file'));
            return;
          }

          const merged = geometries.length === 1 ? geometries[0] : mergeGeometries(geometries);
          if (!merged) {
            reject(new Error('Failed to merge geometries'));
            return;
          }

          if (scale !== 1) merged.scale(scale, scale, scale);
          resolve(merged);
        },
        undefined,
        (error) => reject(error)
      );
    });
  }

  private animate = (): void => {
    this.animationFrameId = requestAnimationFrame(this.animate);
    if (this.object) {
      this.object.rotateOnWorldAxis(new THREE.Vector3(0, 1, 0), 0.0025);
    }
    this.renderer.render(this.scene, this.camera);
  };

  dispose(): void {
    this.disposed = true;
    if (this.animationFrameId !== null) cancelAnimationFrame(this.animationFrameId);
    this.resizeObserver.disconnect();
    if (this.object) this.disposeObject(this.object);
    this.renderer.dispose();
  }
}
